import { FC } from 'react';
import {
  ActionsMenu,
  PageContentLayout,
  PageHeaderLayout,
  Link,
  ListTotal,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Divider, Stack } from '@mui/material';
import { APP, ROUTES } from 'config';
import { useSearchParams } from 'utils/navigation';
import { alpha3ToAlpha2 } from 'i18n-iso-countries';
import {
  adminCardPriceTypes,
  adminCardsFiltersSchema,
  adminCardStatuses,
  COLOR_BY_STATUS,
  getFaceValueLabel,
  useAdminCards,
  useDownloadBulkUpdateTemplate,
  useExportToXls,
} from 'store/admin-cards';
import {
  formatCountry,
  formatList,
  formatNumber,
  formatPercents,
} from 'utils/format';
import { useModalContext } from 'store/modal';
import {
  AdminBrandsFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import moment from 'moment';
import { currencies } from 'types/general';
import { DownloadXlsAction } from 'components/actions';
import { brandProviders } from 'store/brands';
import { CreateCardModal } from 'containers/admin';
import { useRouteMatch } from 'react-router-dom';
import { LanguagesSelectModal } from 'components/modals';
import { BrandCardsBulkUpdateImportModal } from './atoms';

export const AdminCardsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { openModal } = useModalContext();
  const { path } = useRouteMatch();

  const { searchParams: filters, navigate } = useSearchParams(
    adminCardsFiltersSchema,
  );

  const { data: cards, isLoading } = useAdminCards(filters);

  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();
  const { mutate: downloadTemplate, isLoading: isDownloadingTemplate } =
    useDownloadBulkUpdateTemplate();

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('cards.title'),
          },
        ]}
        side={
          <ActionsMenu
            items={[
              {
                label: t('cards.actions.create'),
                onClick: () => {
                  openModal({
                    component: CreateCardModal,
                    size: 'lg',
                  });
                },
              },
              {
                label: t('global.actions.bulk_update'),
                onClick: () => {
                  openModal({
                    component: BrandCardsBulkUpdateImportModal,
                  });
                },
              },
              {
                label: t('dashboard.download_xls'),
                onClick: () => {
                  downloadTemplate();
                },
                disabled: isDownloadingTemplate,
              },
            ]}
          />
        }
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'cards',
                label: t('cards.title'),
                url: ROUTES.admin.cards.list,
                isActive: path === ROUTES.admin.cards.list,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap="10px"
            >
              <ListTotal total={cards?.total} />

              <Stack direction="row" alignItems="center" gap="10px">
                <Filters
                  value={filters}
                  initialValues={adminCardsFiltersSchema.parse({})}
                  onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                  showLabel={false}
                >
                  {({ value, initialValues, setFilter }) => (
                    <>
                      <FilterButton
                        label={t('common.status')}
                        value={value.status}
                        initialValue={initialValues.status}
                        onChange={(v) => setFilter('status', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            name="status"
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple={true}
                            searchable={false}
                            options={adminCardStatuses.map((status) => ({
                              key: status,
                              label: t(`status.${status}`),
                            }))}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('brands.title')}
                        value={value.brands}
                        initialValue={initialValues.brands}
                        onChange={(v) => setFilter('brands', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <AdminBrandsFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('benefits.country')}
                        value={value.countries}
                        initialValue={initialValues.countries}
                        onChange={(v) => setFilter('countries', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            name="countries"
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple
                            options={APP.countries.alpha3.map((alpha3) => ({
                              key: alpha3,
                              label: formatCountry(i18n.language, alpha3),
                            }))}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('providers.title')}
                        value={value.providers}
                        initialValue={initialValues.providers}
                        onChange={(v) => setFilter('providers', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            name="providers"
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple={true}
                            searchable={false}
                            options={brandProviders.map((provider) => ({
                              key: provider,
                              label: provider,
                            }))}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('marketplaceItems.currency')}
                        value={value.currency}
                        initialValue={initialValues.currency}
                        onChange={(v) => setFilter('currency', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            name="currency"
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple={true}
                            options={currencies.map((currency) => ({
                              key: currency,
                              label: currency,
                            }))}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('marketplaceItems.priceType')}
                        value={value.priceType}
                        initialValue={initialValues.priceType}
                        onChange={(v) => setFilter('priceType', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            name="priceType"
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple={true}
                            searchable={false}
                            options={adminCardPriceTypes.map((type) => ({
                              key: type,
                              label: t(`brands.priceType.${type}`),
                            }))}
                          />
                        )}
                      </FilterButton>

                      <OpenSearchFilter
                        value={value.search}
                        onApplyFilter={(v) => setFilter('search', v)}
                        placeholder={t('common.search')}
                      />
                    </>
                  )}
                </Filters>

                <Divider orientation="vertical" variant="middle" flexItem />

                <DownloadXlsAction
                  listLength={cards?.total ?? 0}
                  exportToExcel={async () => {
                    const response = await openModal({
                      component: LanguagesSelectModal,
                    });

                    if (response.action === 'CONFIRM') {
                      exportToXls({
                        filters: {
                          ...filters,
                          languages: response.languages,
                        },
                      });
                    }
                  }}
                  loading={isExportingToXls}
                />
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={cards?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'sequence',
                header: t('integrations.hiBobServiceUserId'),
                cell: ({ row: { original: card } }) => (
                  <Link
                    label={card.sequence}
                    to={ROUTES.admin.cards.settings.replace(':id', card.id)}
                  />
                ),
                meta: { sort: true },
              },
              {
                id: 'status',
                header: t('common.status'),
                cell: ({ row: { original: card } }) => (
                  <Typography color={COLOR_BY_STATUS[card.status]}>
                    {t(`status.${card.status}`)}
                  </Typography>
                ),
                meta: { sort: true },
              },
              {
                id: 'brand_name',
                header: t('brands.title'),
                cell: ({ row: { original: card } }) => card.brand?.name ?? '-',
                meta: { sort: true },
              },
              {
                id: 'countries',
                header: t('marketplaceItems.countries'),
                cell: ({ row: { original: card } }) =>
                  formatList(
                    i18n.language,
                    card.countries.map(
                      (alpha3) => alpha3ToAlpha2(alpha3) ?? alpha3,
                    ),
                  ),
              },
              {
                id: 'faceValue',
                header: t('marketplace.ordersTablePrice'),
                cell: ({ row: { original: card } }) =>
                  getFaceValueLabel({ ...card, language: i18n.language }, 10),
                meta: { align: 'right' },
              },
              {
                id: 'faceValueCurrency',
                header: t('marketplaceItems.currency'),
                cell: ({ row: { original: card } }) => card.faceValueCurrency,
              },
              {
                id: 'melpFeePercentage',
                header: t('cards.fee.label'),
                cell: ({ row: { original: card } }) =>
                  formatPercents(i18n.language, card.melpFeePercentage),
              },
              {
                id: 'melpFeeEuro',
                header: t('cards.fixedFee.label'),
                cell: ({ row: { original: card } }) =>
                  formatNumber(i18n.language, card.melpFeeEuro / 100),
              },
              {
                id: 'provider_id',
                header: t('providers.title'),
                cell: ({ row: { original: card } }) => card.provider,
                meta: { sort: true },
              },
              {
                id: 'purchasePriceDiscountPercentage',
                header: t('marketplaceItems.discountPercentage'),
                cell: ({ row: { original: card } }) =>
                  formatPercents(
                    i18n.language,
                    card.purchasePriceDiscountPercentage,
                  ),
                meta: { align: 'right' },
              },
              {
                id: 'ordersCount',
                header: t('menu.orders'),
                cell: ({ row: { original: card } }) => card.ordersCount,
                meta: { align: 'right' },
              },
              {
                id: 'creation',
                header: t('marketplaceOrders.orderDate'),
                cell: ({ row: { original: code } }) =>
                  moment(code.createdAt).format('l'),
                meta: { sort: true },
              },
            ]}
            sort={{
              value: filters.sort,
              onChange: (v) => navigate({ params: { sort: v } }),
            }}
            pagination={
              cards && {
                count: cards.total,
                page: filters.page,
                pageSize: filters.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
